/**
 * The Upcoming watchlist's requests and grouping.
 *
 * Whether a watchlist game counts as logged (imported_event_id set, OR its
 * ESPN id already in events) and which timezone a venue is in are the
 * server's answers (upcoming_logic, sports_tracker.tz). The browser shows
 * `is_logged` and `start_time` as sent.
 */
import { authedFetch } from "./api";
import type { EspnGame } from "./addEvent";
import { monthLabel } from "./format";
import { orThrow } from "./http";

export type Rsvp = "yes" | "maybe" | "no" | null;

export const RSVP_CHOICES: { value: Exclude<Rsvp, null>; label: string }[] = [
  { value: "yes", label: "Going" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Not going" },
];

/** The filter chips, as the query param each sends. "none" means no answer yet. */
export const RSVP_FILTERS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Going" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Not going" },
  { value: "none", label: "No answer" },
] as const;
export type RsvpFilter = (typeof RSVP_FILTERS)[number]["value"];

export interface UpcomingRow {
  id: number;
  event_date: string;
  league: string;
  home_team: string;
  away_team: string;
  venue_name: string | null;
  venue_city: string | null;
  /** Venue-local, e.g. "7:00 PM"; null when the zone is unknown. */
  start_time: string | null;
  rsvp: Rsvp;
  url: string | null;
  notes: string | null;
  game_type: string;
  is_logged: boolean;
  sport_emoji: string;
}

export interface UpcomingDto {
  games: UpcomingRow[];
  logos: Record<string, string>;
  venue_images: Record<string, string>;
  /** The earliest date shown: today, or 60 days back with past games on. */
  since: string;
  espn_leagues: string[];
  game_types: string[];
}

export interface Team {
  espn_team_id: string;
  display_name: string;
}

export async function fetchUpcoming(rsvp: RsvpFilter, past: boolean): Promise<UpcomingDto> {
  const sp = new URLSearchParams();
  if (rsvp !== "all") sp.set("rsvp", rsvp);
  if (past) sp.set("past", "1");
  const qs = sp.toString();
  const res = await authedFetch(`/api/upcoming${qs ? `?${qs}` : ""}`);
  return (await orThrow(res, "Could not load the watchlist")) as UpcomingDto;
}

async function send(path: string, method: string, body: unknown, what: string): Promise<unknown> {
  const res = await authedFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return orThrow(res, what);
}

export const setRsvp = (id: number, rsvp: Rsvp) => send(`/api/upcoming/${id}`, "PATCH", { rsvp }, "Could not save the RSVP");

export interface UpcomingEdit {
  event_date: string;
  home_team: string;
  away_team: string;
  venue_name: string;
  venue_city: string;
  url: string;
  notes: string;
}

export const updateUpcoming = (id: number, e: UpcomingEdit) => send(`/api/upcoming/${id}`, "PATCH", e, "Could not save the game");
export const deleteUpcoming = (id: number) => send(`/api/upcoming/${id}`, "DELETE", undefined, "Could not remove the game");

export async function fetchTeams(league: string): Promise<Team[]> {
  const res = await authedFetch(`/api/espn/teams?${new URLSearchParams({ league })}`);
  return (await orThrow(res, "ESPN request failed")) as Team[];
}

export async function fetchSchedule(league: string, teamId: string): Promise<EspnGame[]> {
  const res = await authedFetch(`/api/espn/schedule?${new URLSearchParams({ league, team_id: teamId })}`);
  return (await orThrow(res, "ESPN request failed")) as EspnGame[];
}

/** Put ESPN games on the watchlist. The server re-reads each from ESPN by id. */
export const addFromEspn = (league: string, ids: string[]) =>
  send("/api/upcoming", "POST", { league, espn_event_ids: ids }, "Could not add the games");

export interface ManualUpcoming extends UpcomingEdit {
  league: string;
  game_type: string;
}

export const addManual = (g: ManualUpcoming) => send("/api/upcoming/manual", "POST", g, "Could not add the game");

export interface MonthGroup {
  month: string;
  games: UpcomingRow[];
}

/** Games under month headers, in the order sent (soonest first). */
export function byMonth(games: UpcomingRow[]): MonthGroup[] {
  const out: MonthGroup[] = [];
  for (const g of games) {
    const m = monthLabel(g.event_date);
    const last = out[out.length - 1];
    if (last && last.month === m) last.games.push(g);
    else out.push({ month: m, games: [g] });
  }
  return out;
}
